import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Building2, Users, CheckCircle2, X, Clock, UserCheck, UserX,
  MapPin, AlertCircle, ChevronRight, Plus, Settings, BarChart3,
  Shield, Stethoscope, Edit, Trash2, Save, Loader2, Package,
  FlaskConical, TrendingUp, TrendingDown, Minus
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = {
  secretario: "Secretário(a)", coordenador: "Coordenador(a) APS",
  enfermeiro: "Enfermeiro(a)", medico: "Médico(a)", tecnico: "Técnico(a) Enf.",
  acs: "ACS", especialista: "Especialista", admin: "Administrador"
};
const ROLE_COLORS = {
  secretario: "bg-purple-100 text-purple-800", coordenador: "bg-blue-100 text-blue-800",
  enfermeiro: "bg-green-100 text-green-800", medico: "bg-sky-100 text-sky-800",
  tecnico: "bg-teal-100 text-teal-800", acs: "bg-orange-100 text-orange-800",
  especialista: "bg-pink-100 text-pink-800", admin: "bg-gray-800 text-white",
};
const ROLES = [
  { value: "secretario", label: "Secretário(a)" }, { value: "coordenador", label: "Coordenador(a) APS" },
  { value: "enfermeiro", label: "Enfermeiro(a)" }, { value: "medico", label: "Médico(a)" },
  { value: "tecnico", label: "Técnico(a) Enf." }, { value: "acs", label: "ACS" },
  { value: "especialista", label: "Especialista" }, { value: "admin", label: "Admin Sistema" },
];
const UNIT_TYPES = ["UBS", "UBSF", "ESF", "CAPS-AD", "hospital", "outro"];
const emptyUnit = { name: "", address: "", unit_type: "UBS" };

const MATERIAL_CATEGORIES = [
  { value: "coberturas_primarias", label: "Coberturas Primárias" },
  { value: "coberturas_secundarias", label: "Coberturas Secundárias" },
  { value: "limpeza", label: "Limpeza" },
  { value: "desbridamento", label: "Desbridamento" },
  { value: "fixacao", label: "Fixação" },
  { value: "outros", label: "Outros" },
];
const MATERIAL_UNITS = [
  { value: "un", label: "Unidade (un)" }, { value: "pct", label: "Pacote (pct)" },
  { value: "rl", label: "Rolo (rl)" }, { value: "fr", label: "Frasco (fr)" },
  { value: "tb", label: "Tubo (tb)" }, { value: "m", label: "Metro (m)" },
];

const CATEGORY_COLORS = {
  coberturas_primarias: "bg-blue-100 text-blue-800",
  coberturas_secundarias: "bg-sky-100 text-sky-800",
  limpeza: "bg-green-100 text-green-800",
  desbridamento: "bg-amber-100 text-amber-800",
  fixacao: "bg-purple-100 text-purple-800",
  outros: "bg-gray-100 text-gray-700",
};

export default function GestaoMunicipalPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const isMunicipalManager = ["secretario", "coordenador"].includes(user?.role);

  const [activeTab, setActiveTab] = useState("validacoes");
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [municipalities, setMunicipalities] = useState([]);
  const [healthUnits, setHealthUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [editingUser, setEditingUser] = useState(null);
  const [editRole, setEditRole] = useState("");
  const [saving, setSaving] = useState(false);
  const [filterMunicipality, setFilterMunicipality] = useState("");

  // Health unit CRUD state
  const [unitForm, setUnitForm] = useState(emptyUnit);
  const [editingUnit, setEditingUnit] = useState(null);
  const [showUnitForm, setShowUnitForm] = useState(false);
  const [savingUnit, setSavingUnit] = useState(false);
  const [deletingUnit, setDeletingUnit] = useState(null);

  // Materials state
  const [materials, setMaterials] = useState([]);
  const [materialStats, setMaterialStats] = useState([]);
  const [materialForecast, setMaterialForecast] = useState(null);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [materialForm, setMaterialForm] = useState({ name: "", category: "coberturas_primarias", unit: "un" });
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [savingMaterial, setSavingMaterial] = useState(false);
  const [showMaterialForm, setShowMaterialForm] = useState(false);
  const [matStatsLoading, setMatStatsLoading] = useState(false);

  const fetchMaterials = useCallback(async () => {
    try {
      const [mRes, sRes] = await Promise.all([
        axios.get(`${API}/municipality-materials`, { withCredentials: true }),
        axios.get(`${API}/municipality-materials/stats`, { withCredentials: true }),
      ]);
      setMaterials(mRes.data);
      setMaterialStats(sRes.data);
    } catch {}
  }, []);

  const handleSaveMaterial = async () => {
    if (!materialForm.name.trim()) return;
    setSavingMaterial(true);
    try {
      if (editingMaterial) {
        await axios.put(`${API}/municipality-materials/${editingMaterial.material_id}`, materialForm, { withCredentials: true });
      } else {
        await axios.post(`${API}/municipality-materials`, materialForm, { withCredentials: true });
      }
      setMaterialForm({ name: "", category: "coberturas_primarias", unit: "un" });
      setEditingMaterial(null);
      setShowMaterialForm(false);
      await fetchMaterials();
    } catch (e) {
      alert(e.response?.data?.detail || "Erro ao salvar material");
    }
    setSavingMaterial(false);
  };

  const handleDeleteMaterial = async (materialId) => {
    if (!window.confirm("Remover este material?")) return;
    try {
      await axios.delete(`${API}/municipality-materials/${materialId}`, { withCredentials: true });
      await fetchMaterials();
    } catch {}
  };

  const toggleMaterialActive = async (mat) => {
    try {
      await axios.put(`${API}/municipality-materials/${mat.material_id}`, { is_active: !mat.is_active }, { withCredentials: true });
      await fetchMaterials();
    } catch {}
  };

  useEffect(() => {
    if (!["admin", "secretario", "coordenador"].includes(user?.role)) {
      navigate("/dashboard");
      return;
    }
    fetchData();
    fetchMaterials();
  }, [user, navigate, fetchMaterials]);

  useEffect(() => {
    if (activeTab === "materiais" && !materialForecast && !forecastLoading) {
      setForecastLoading(true);
      axios.get(`${API}/municipality-materials/forecast`, { withCredentials: true })
        .then(r => setMaterialForecast(r.data))
        .catch(() => setMaterialForecast({ forecast: [], unit_summary: [], total_materials_tracked: 0 }))
        .finally(() => setForecastLoading(false));
    }
  }, [activeTab, materialForecast, forecastLoading]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const reqs = [
        axios.get(`${API}/admin/pending-validations`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
      ];
      if (isAdmin) reqs.push(axios.get(`${API}/admin/municipalities`, { withCredentials: true }));
      const results = await Promise.all(reqs);
      setPendingUsers(results[0].data || []);
      setAllUsers(results[1].data || []);
      if (isAdmin && results[2]) setMunicipalities(results[2].data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchHealthUnits = async () => {
    const mun = isAdmin ? filterMunicipality : user?.municipality;
    const url = mun ? `${API}/health-units?municipality=${encodeURIComponent(mun)}` : `${API}/health-units`;
    const r = await axios.get(url, { withCredentials: true });
    setHealthUnits(r.data || []);
  };

  useEffect(() => {
    if (activeTab === "unidades") fetchHealthUnits();
  }, [activeTab, filterMunicipality]);

  const handleValidate = async (userId, status, reason = "") => {
    setValidating(userId);
    try {
      await axios.put(`${API}/admin/users/${userId}/validate`, { status, reason }, { withCredentials: true });
      setPendingUsers(prev => prev.filter(u => u.user_id !== userId));
      await fetchData();
      setRejectModal(null); setRejectReason("");
    } catch (e) { console.error(e); }
    finally { setValidating(null); }
  };

  const handleUpdateRole = async () => {
    if (!editingUser || !editRole) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/users/${editingUser.user_id}`, { role: editRole }, { withCredentials: true });
      await fetchData();
      setEditingUser(null);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleSaveUnit = async () => {
    if (!unitForm.name.trim()) return;
    setSavingUnit(true);
    try {
      if (editingUnit) {
        await axios.put(`${API}/health-units/${editingUnit.unit_id}`, unitForm, { withCredentials: true });
      } else {
        await axios.post(`${API}/health-units`, unitForm, { withCredentials: true });
      }
      setShowUnitForm(false);
      setEditingUnit(null);
      setUnitForm(emptyUnit);
      await fetchHealthUnits();
    } catch (e) { console.error(e); }
    finally { setSavingUnit(false); }
  };

  const handleDeleteUnit = async (unitId) => {
    setDeletingUnit(unitId);
    try {
      await axios.delete(`${API}/health-units/${unitId}`, { withCredentials: true });
      await fetchHealthUnits();
    } catch (e) { console.error(e); }
    finally { setDeletingUnit(null); }
  };

  const startEditUnit = (unit) => {
    setEditingUnit(unit);
    setUnitForm({ name: unit.name, address: unit.address || "", unit_type: unit.unit_type || "UBS" });
    setShowUnitForm(true);
  };

  const approvedManagers = allUsers.filter(u => ["secretario", "coordenador"].includes(u.role) && u.is_validated);
  const displayedUsers = filterMunicipality
    ? allUsers.filter(u => u.municipality === filterMunicipality)
    : allUsers;
  const uniqueMunicipalitiesInUsers = [...new Set(allUsers.map(u => u.municipality).filter(Boolean))].sort();

  const tabs = isAdmin
    ? [
        { id: "validacoes", label: "Validações", badge: pendingUsers.length },
        { id: "usuarios", label: "Usuários" },
        { id: "unidades", label: "Unidades" },
        { id: "materiais", label: "Materiais" },
        { id: "municipios", label: "Municípios" },
      ]
    : [
        { id: "validacoes", label: "Validações", badge: pendingUsers.length },
        { id: "usuarios", label: "Usuários" },
        { id: "unidades", label: "Unidades" },
        { id: "materiais", label: "Materiais" },
      ];

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="animate-fade-in">
          <div className="flex items-center justify-between gap-3 mb-1">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center flex-shrink-0">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-foreground" style={{ fontFamily: "Manrope" }}>
                  {isAdmin ? "Gestão do Sistema" : `Gestão Municipal — ${user?.municipality || ""}${user?.state ? ` / ${user.state}` : ""}`}
                </h1>
                <p className="text-xs text-muted-foreground">
                  {isAdmin ? "Administrador do sistema · acesso total" : `${ROLE_LABELS[user?.role] || user?.role}`}
                </p>
              </div>
            </div>
            {isAdmin && (
              <button
                onClick={() => navigate("/gestao-municipal/cadastrar-gestor")}
                className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-semibold hover:bg-primary/90 transition-all flex-shrink-0"
                data-testid="cadastrar-gestor-btn"
              >
                <Plus className="w-4 h-4" />
                Cadastrar Gestor
              </button>
            )}
          </div>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 animate-fade-in">
          <div className="bg-white border border-border rounded-xl p-4" data-testid="stat-pending">
            <p className="text-2xl font-bold text-foreground">{pendingUsers.length}</p>
            <p className="text-xs text-muted-foreground mt-1">Aguardando Validação</p>
          </div>
          <div className="bg-white border border-border rounded-xl p-4" data-testid="stat-total-users">
            <p className="text-2xl font-bold text-foreground">{allUsers.filter(u => u.is_validated).length}</p>
            <p className="text-xs text-muted-foreground mt-1">Usuários Ativos</p>
          </div>
          <div className="bg-white border border-border rounded-xl p-4" data-testid="stat-managers">
            <p className="text-2xl font-bold text-foreground">{approvedManagers.length}</p>
            <p className="text-xs text-muted-foreground mt-1">Gestores Municipais</p>
          </div>
          <div className="bg-white border border-border rounded-xl p-4" data-testid="stat-municipalities">
            <p className="text-2xl font-bold text-foreground">
              {isAdmin ? municipalities.length : (user?.municipality ? 1 : "—")}
            </p>
            <p className="text-xs text-muted-foreground mt-1">{isAdmin ? "Municípios" : "Seu Município"}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl border border-border overflow-hidden animate-fade-in">
          <div className="flex border-b border-border overflow-x-auto">
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 flex-shrink-0 py-3 px-4 text-sm font-medium transition-all ${activeTab === tab.id ? "text-primary border-b-2 border-primary bg-primary/5" : "text-muted-foreground hover:text-foreground"}`}
                data-testid={`tab-${tab.id}`}>
                {tab.label}
                {tab.badge > 0 && (
                  <span className="bg-destructive text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">{tab.badge}</span>
                )}
              </button>
            ))}
          </div>

          <div className="p-5">
            {/* Tab: Validações */}
            {activeTab === "validacoes" && (
              <div className="space-y-3">
                {loading ? (
                  <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-20 bg-secondary/50 rounded-xl animate-pulse" />)}</div>
                ) : pendingUsers.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
                    <p className="text-base font-semibold text-foreground">Nenhuma validação pendente</p>
                    <p className="text-sm text-muted-foreground mt-1">Todos os profissionais foram validados.</p>
                  </div>
                ) : (
                  pendingUsers.map(u => (
                    <div key={u.user_id} className="border border-border rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`pending-user-${u.user_id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-foreground">{u.full_name || u.name}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${ROLE_COLORS[u.professional_category] || "bg-gray-100 text-gray-700"}`}>
                            {ROLE_LABELS[u.professional_category] || u.professional_category}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{u.email}</p>
                        <div className="flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3 text-muted-foreground" />
                          <p className="text-xs text-muted-foreground">
                            {u.municipality && u.state ? `${u.municipality} / ${u.state}` : u.municipality || u.state || "Localização não informada"}
                          </p>
                        </div>
                        {u.council_number && <p className="text-xs text-muted-foreground">Registro: {u.council_number}</p>}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleValidate(u.user_id, "aprovado")}
                          disabled={validating === u.user_id}
                          className="flex items-center gap-1.5 px-3 py-2 bg-green-50 text-green-700 border border-green-200 rounded-lg text-xs font-medium hover:bg-green-100 transition-all disabled:opacity-60"
                          data-testid={`approve-${u.user_id}`}
                        >
                          <UserCheck className="w-3.5 h-3.5" />Aprovar
                        </button>
                        <button
                          onClick={() => setRejectModal(u.user_id)}
                          className="flex items-center gap-1.5 px-3 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg text-xs font-medium hover:bg-red-100 transition-all"
                          data-testid={`reject-${u.user_id}`}
                        >
                          <UserX className="w-3.5 h-3.5" />Rejeitar
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Tab: Usuários */}
            {activeTab === "usuarios" && (
              <div className="space-y-3">
                {isAdmin && uniqueMunicipalitiesInUsers.length > 0 && (
                  <div className="flex items-center gap-2">
                    <select value={filterMunicipality} onChange={e => setFilterMunicipality(e.target.value)}
                      className="h-9 border border-border rounded-lg px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                      data-testid="filter-municipality-select">
                      <option value="">Todos os municípios</option>
                      {uniqueMunicipalitiesInUsers.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                    {filterMunicipality && (
                      <button onClick={() => setFilterMunicipality("")} className="text-xs text-muted-foreground hover:text-foreground">Limpar filtro</button>
                    )}
                  </div>
                )}
                {loading ? (
                  <div className="space-y-2">{[1,2,3,4].map(i => <div key={i} className="h-14 bg-secondary/50 rounded-xl animate-pulse" />)}</div>
                ) : displayedUsers.length === 0 ? (
                  <div className="text-center py-8"><Users className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" /><p className="text-sm text-muted-foreground">Nenhum usuário encontrado</p></div>
                ) : (
                  displayedUsers.map(u => (
                    <div key={u.user_id} className="border border-border rounded-xl p-3 flex items-center gap-3" data-testid={`user-row-${u.user_id}`}>
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 text-xs font-bold text-primary">
                        {(u.full_name || u.name || "?")[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{u.full_name || u.name}</p>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${ROLE_COLORS[u.role] || "bg-gray-100 text-gray-700"}`}>
                            {ROLE_LABELS[u.role] || u.role}
                          </span>
                          {u.municipality && <span className="text-xs text-muted-foreground">{u.municipality}/{u.state}</span>}
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${u.is_validated ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                            {u.is_validated ? "Ativo" : "Pendente"}
                          </span>
                        </div>
                      </div>
                      <button onClick={() => { setEditingUser(u); setEditRole(u.role || ""); }}
                        className="p-1.5 rounded-lg hover:bg-secondary transition-all flex-shrink-0"
                        title="Alterar função" data-testid={`edit-user-${u.user_id}`}>
                        <Edit className="w-3.5 h-3.5 text-muted-foreground" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Tab: Unidades de Saúde */}
            {activeTab === "unidades" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground">
                      Unidades — {isMunicipalManager ? user?.municipality : (filterMunicipality || "Todos os municípios")}
                    </p>
                    <p className="text-xs text-muted-foreground">{healthUnits.length} unidade(s) cadastrada(s)</p>
                  </div>
                  {isAdmin && (
                    <select value={filterMunicipality} onChange={e => setFilterMunicipality(e.target.value)}
                      className="h-9 border border-border rounded-lg px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                      data-testid="unit-filter-municipality">
                      <option value="">Todos municípios</option>
                      {uniqueMunicipalitiesInUsers.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  )}
                  <button
                    onClick={() => { setShowUnitForm(true); setEditingUnit(null); setUnitForm(emptyUnit); }}
                    className="flex items-center gap-1.5 px-3 py-2 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:bg-primary/90 transition-all flex-shrink-0"
                    data-testid="add-unit-btn"
                  >
                    <Plus className="w-3.5 h-3.5" /> Nova Unidade
                  </button>
                </div>

                {/* Form to create/edit a unit */}
                {showUnitForm && (
                  <div className="border border-primary/30 bg-primary/5 rounded-xl p-4 space-y-3 animate-fade-in" data-testid="unit-form">
                    <p className="text-sm font-semibold text-foreground">
                      {editingUnit ? "Editar Unidade" : "Nova Unidade de Saúde"}
                    </p>
                    <div>
                      <label className="text-xs text-muted-foreground">Nome da Unidade *</label>
                      <input
                        type="text"
                        value={unitForm.name}
                        onChange={e => setUnitForm(p => ({ ...p, name: e.target.value }))}
                        placeholder="Ex: UBS Vila Nova"
                        className="mt-1 w-full h-10 border border-border rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                        data-testid="unit-name-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Endereço</label>
                      <input
                        type="text"
                        value={unitForm.address}
                        onChange={e => setUnitForm(p => ({ ...p, address: e.target.value }))}
                        placeholder="Rua, número, bairro"
                        className="mt-1 w-full h-10 border border-border rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                        data-testid="unit-address-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Tipo</label>
                      <select
                        value={unitForm.unit_type}
                        onChange={e => setUnitForm(p => ({ ...p, unit_type: e.target.value }))}
                        className="mt-1 w-full h-10 border border-border rounded-lg px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                        data-testid="unit-type-select"
                      >
                        {UNIT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={() => { setShowUnitForm(false); setEditingUnit(null); setUnitForm(emptyUnit); }}
                        className="flex-1 py-2 border border-border rounded-lg text-xs font-medium hover:bg-secondary transition-all"
                      >Cancelar</button>
                      <button
                        onClick={handleSaveUnit}
                        disabled={savingUnit || !unitForm.name.trim()}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:bg-primary/90 transition-all disabled:opacity-60"
                        data-testid="save-unit-btn"
                      >
                        {savingUnit ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        {editingUnit ? "Salvar Edição" : "Cadastrar"}
                      </button>
                    </div>
                  </div>
                )}

                {healthUnits.length === 0 && !showUnitForm ? (
                  <div className="text-center py-10">
                    <Building2 className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-sm font-medium text-foreground">Nenhuma unidade cadastrada</p>
                    <p className="text-xs text-muted-foreground mt-1">Clique em "Nova Unidade" para cadastrar.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {healthUnits.map(unit => (
                      <div key={unit.unit_id} className="border border-border rounded-xl p-4 flex items-center justify-between gap-3" data-testid={`unit-row-${unit.unit_id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-4 h-4 text-primary" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-foreground">{unit.name}</p>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-xs bg-secondary px-1.5 py-0.5 rounded text-muted-foreground">{unit.unit_type}</span>
                              {unit.address && <span className="text-xs text-muted-foreground truncate">{unit.address}</span>}
                              {isAdmin && unit.municipality && <span className="text-xs text-muted-foreground">{unit.municipality}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => startEditUnit(unit)}
                            className="p-1.5 rounded-lg hover:bg-secondary transition-all"
                            title="Editar"
                            data-testid={`edit-unit-${unit.unit_id}`}
                          >
                            <Edit className="w-3.5 h-3.5 text-muted-foreground" />
                          </button>
                          <button
                            onClick={() => handleDeleteUnit(unit.unit_id)}
                            disabled={deletingUnit === unit.unit_id}
                            className="p-1.5 rounded-lg hover:bg-red-50 transition-all"
                            title="Excluir"
                            data-testid={`delete-unit-${unit.unit_id}`}
                          >
                            {deletingUnit === unit.unit_id
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
                              : <Trash2 className="w-3.5 h-3.5 text-red-500" />}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Tab: Materiais */}
            {activeTab === "materiais" && (
              <div className="space-y-4">
                {/* Header + botão adicionar */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-foreground">Materiais de Curativo Disponíveis</p>
                    <p className="text-xs text-muted-foreground">{materials.filter(m => m.is_active).length} ativo(s) · {materials.length} total</p>
                  </div>
                  <button
                    onClick={() => { setEditingMaterial(null); setMaterialForm({ name: "", category: "coberturas_primarias", unit: "un" }); setShowMaterialForm(true); }}
                    className="flex items-center gap-1.5 px-3 py-2 bg-primary text-primary-foreground rounded-xl text-xs font-semibold hover:bg-primary/90 transition-all"
                    data-testid="add-material-btn"
                  >
                    <Plus className="w-3.5 h-3.5" /> Adicionar Material
                  </button>
                </div>

                {/* Formulário de material */}
                {showMaterialForm && (
                  <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 space-y-3 animate-fade-in">
                    <p className="text-xs font-semibold text-primary">{editingMaterial ? "Editar Material" : "Novo Material"}</p>
                    <div>
                      <label className="text-xs text-muted-foreground">Nome do material *</label>
                      <input
                        value={materialForm.name}
                        onChange={e => setMaterialForm(p => ({ ...p, name: e.target.value }))}
                        placeholder="Ex: Alginato de Cálcio, Hidrogel, SF 0,9%..."
                        className="mt-1 w-full h-10 border border-border rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                        data-testid="material-name-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-muted-foreground">Categoria</label>
                        <select
                          value={materialForm.category}
                          onChange={e => setMaterialForm(p => ({ ...p, category: e.target.value }))}
                          className="mt-1 w-full h-10 border border-border rounded-lg px-3 text-sm bg-white focus:outline-none"
                          data-testid="material-category-select"
                        >
                          {MATERIAL_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Unidade</label>
                        <select
                          value={materialForm.unit}
                          onChange={e => setMaterialForm(p => ({ ...p, unit: e.target.value }))}
                          className="mt-1 w-full h-10 border border-border rounded-lg px-3 text-sm bg-white focus:outline-none"
                          data-testid="material-unit-select"
                        >
                          {MATERIAL_UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => { setShowMaterialForm(false); setEditingMaterial(null); }}
                        className="flex-1 py-2 border border-border rounded-xl text-xs font-medium hover:bg-secondary transition-all">
                        Cancelar
                      </button>
                      <button onClick={handleSaveMaterial} disabled={savingMaterial || !materialForm.name.trim()}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-primary text-primary-foreground rounded-xl text-xs font-semibold hover:bg-primary/90 disabled:opacity-60 transition-all"
                        data-testid="save-material-btn">
                        {savingMaterial ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        {savingMaterial ? "Salvando..." : "Salvar"}
                      </button>
                    </div>
                  </div>
                )}

                {/* Lista de materiais agrupados por categoria */}
                {MATERIAL_CATEGORIES.map(cat => {
                  const catMaterials = materials.filter(m => m.category === cat.value);
                  if (!catMaterials.length) return null;
                  return (
                    <div key={cat.value}>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{cat.label}</p>
                      <div className="space-y-2">
                        {catMaterials.map(mat => (
                          <div key={mat.material_id}
                            className={`flex items-center justify-between gap-3 border rounded-xl px-4 py-3 ${mat.is_active ? "border-border bg-white" : "border-border/50 bg-secondary/30 opacity-60"}`}
                            data-testid={`material-row-${mat.material_id}`}>
                            <div className="flex items-center gap-3 min-w-0">
                              <FlaskConical className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                              <div className="min-w-0">
                                <p className={`text-sm font-medium truncate ${mat.is_active ? "text-foreground" : "text-muted-foreground line-through"}`}>{mat.name}</p>
                                <p className="text-xs text-muted-foreground">{MATERIAL_UNITS.find(u => u.value === mat.unit)?.label || mat.unit}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <button onClick={() => toggleMaterialActive(mat)}
                                className={`text-xs px-2.5 py-1 rounded-lg font-medium transition-all ${mat.is_active ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-secondary text-muted-foreground hover:bg-secondary/80"}`}>
                                {mat.is_active ? "Ativo" : "Inativo"}
                              </button>
                              <button onClick={() => { setEditingMaterial(mat); setMaterialForm({ name: mat.name, category: mat.category, unit: mat.unit }); setShowMaterialForm(true); }}
                                className="p-1.5 rounded-lg hover:bg-secondary transition-all">
                                <Edit className="w-3.5 h-3.5 text-muted-foreground" />
                              </button>
                              <button onClick={() => handleDeleteMaterial(mat.material_id)}
                                className="p-1.5 rounded-lg hover:bg-destructive/10 transition-all">
                                <Trash2 className="w-3.5 h-3.5 text-destructive" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}

                {materials.length === 0 && (
                  <div className="text-center py-10">
                    <Package className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-sm font-medium text-foreground">Nenhum material cadastrado</p>
                    <p className="text-xs text-muted-foreground mt-1">Adicione os materiais disponíveis no seu município para rastrear o consumo nas evoluções.</p>
                  </div>
                )}

                {/* Estatísticas de consumo */}
                {materialStats.length > 0 && (
                  <div className="mt-6">
                    <div className="flex items-center gap-2 mb-3">
                      <TrendingUp className="w-4 h-4 text-primary" />
                      <p className="text-sm font-semibold text-foreground">Consumo de Materiais</p>
                    </div>
                    <div className="space-y-2">
                      {materialStats.slice(0, 10).map(stat => (
                        <div key={stat.material_id} className="bg-white border border-border rounded-xl p-4">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-sm font-medium text-foreground">{stat.material_name}</p>
                            <span className="text-sm font-bold text-primary">{stat.total} {stat.unit}</span>
                          </div>
                          {stat.by_unit?.slice(0, 3).map((u, i) => (
                            <div key={i} className="flex justify-between text-xs text-muted-foreground mt-1">
                              <span>{u.unit_name || "Unidade"}</span>
                              <span>{u.quantity} {stat.unit}</span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Previsão de compras */}
                <div className="mt-6">
                  <div className="flex items-center gap-2 mb-3">
                    <BarChart3 className="w-4 h-4 text-emerald-600" />
                    <p className="text-sm font-semibold text-foreground">Previsão de Compras — Próximo Mês</p>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-3">
                    <p className="text-xs text-emerald-800">Baseado na média ponderada dos últimos 3 meses. Meses recentes têm maior peso na previsão.</p>
                  </div>
                  {forecastLoading ? (
                    <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-16 bg-secondary/50 rounded-xl animate-pulse" />)}</div>
                  ) : !materialForecast || materialForecast.total_materials_tracked === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Package className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">Sem histórico suficiente para previsão</p>
                      <p className="text-xs mt-1">Registre materiais nas evoluções para gerar previsões</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {materialForecast.forecast.map(item => (
                        <div key={item.material_id} className="bg-white border border-border rounded-xl p-4">
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-foreground truncate">{item.material_name}</p>
                              <p className="text-xs text-muted-foreground">
                                Média 3m: {item.avg_monthly} {item.unit} · Total acumulado: {item.total_3mo} {item.unit}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <div className="text-right">
                                <p className="text-base font-bold text-emerald-700">{item.forecast_next_month} {item.unit}</p>
                                <p className="text-xs text-muted-foreground">próx. mês</p>
                              </div>
                              {item.trend === "crescente" && (
                                <span className="flex items-center gap-0.5 text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                                  <TrendingUp className="w-3 h-3" />+{Math.abs(item.trend_pct)}%
                                </span>
                              )}
                              {item.trend === "decrescente" && (
                                <span className="flex items-center gap-0.5 text-xs font-semibold text-red-700 bg-red-100 px-2 py-0.5 rounded-full">
                                  <TrendingDown className="w-3 h-3" />-{Math.abs(item.trend_pct)}%
                                </span>
                              )}
                              {item.trend === "estavel" && (
                                <span className="flex items-center gap-0.5 text-xs font-semibold text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">
                                  <Minus className="w-3 h-3" />Estável
                                </span>
                              )}
                            </div>
                          </div>
                          {item.by_unit?.length > 0 && (
                            <div className="pt-2 border-t border-border/50 space-y-1">
                              <p className="text-xs font-medium text-muted-foreground mb-1">Por unidade:</p>
                              {item.by_unit.map((u, i) => (
                                <div key={i} className="flex items-center gap-2">
                                  <p className="text-xs text-foreground w-32 truncate">{u.unit_name}</p>
                                  <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
                                    <div className="h-full bg-emerald-400 rounded-full" style={{ width: `${u.share_pct}%` }} />
                                  </div>
                                  <p className="text-xs font-semibold text-emerald-700 w-12 text-right">{u.forecast_qty} {item.unit}</p>
                                  <p className="text-xs text-muted-foreground w-8 text-right">{u.share_pct}%</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}

                      {/* Resumo por unidade */}
                      {materialForecast.unit_summary?.length > 0 && (
                        <div className="bg-white border border-border rounded-xl p-4 mt-4">
                          <p className="text-sm font-semibold text-foreground mb-3">Total de Materiais Previstos por Unidade</p>
                          <div className="space-y-2">
                            {materialForecast.unit_summary.map((u, i) => {
                              const total = materialForecast.unit_summary.reduce((s, x) => s + x.forecast_total, 0) || 1;
                              const pct = Math.round((u.forecast_total / total) * 100);
                              return (
                                <div key={i} className="flex items-center gap-3" data-testid={`unit-forecast-${i}`}>
                                  <p className="text-xs font-medium text-foreground w-36 truncate">{u.unit_name}</p>
                                  <div className="flex-1 h-3 bg-secondary rounded-full overflow-hidden">
                                    <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                                  </div>
                                  <p className="text-xs font-bold text-emerald-700 w-8 text-right">{pct}%</p>
                                  <p className="text-xs text-muted-foreground w-20 text-right">{u.forecast_total} itens</p>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            {activeTab === "municipios" && isAdmin && (
              <div className="space-y-3">
                {loading ? (
                  <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-16 bg-secondary/50 rounded-xl animate-pulse" />)}</div>
                ) : municipalities.length === 0 ? (
                  <div className="text-center py-10">
                    <Building2 className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-sm font-medium text-foreground">Nenhum município cadastrado ainda</p>
                    <p className="text-xs text-muted-foreground mt-1">Os municípios aparecem automaticamente quando profissionais completam seus cadastros.</p>
                  </div>
                ) : (
                  <>
                    <p className="text-xs text-muted-foreground">{municipalities.length} município(s) com usuários cadastrados</p>
                    {municipalities.map((m, i) => (
                      <div key={i} className="border border-border rounded-xl p-4 flex items-center justify-between gap-3" data-testid={`municipality-row-${m.municipality}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <MapPin className="w-4 h-4 text-primary" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-foreground">{m.municipality}</p>
                            <p className="text-xs text-muted-foreground">{m.state}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 flex-shrink-0 text-right">
                          <div>
                            <p className="text-sm font-bold text-foreground">{m.total_users}</p>
                            <p className="text-xs text-muted-foreground">usuários</p>
                          </div>
                          {m.pending > 0 && (
                            <div>
                              <p className="text-sm font-bold text-orange-600">{m.pending}</p>
                              <p className="text-xs text-muted-foreground">pendentes</p>
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-bold text-purple-600">{m.managers}</p>
                            <p className="text-xs text-muted-foreground">gestores</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Quick links */}
        <div className="grid grid-cols-2 gap-3 animate-fade-in">
          <button onClick={() => navigate("/municipal")}
            className="bg-white border border-border rounded-xl p-4 flex items-center gap-3 hover:bg-secondary/30 transition-all text-left"
            data-testid="link-stats">
            <BarChart3 className="w-8 h-8 text-primary bg-primary/10 rounded-lg p-1.5" />
            <div><p className="text-sm font-semibold text-foreground">Estatísticas</p><p className="text-xs text-muted-foreground">Painel municipal</p></div>
          </button>
          <button onClick={() => navigate("/admin")}
            className="bg-white border border-border rounded-xl p-4 flex items-center gap-3 hover:bg-secondary/30 transition-all text-left"
            data-testid="link-admin">
            <Settings className="w-8 h-8 text-muted-foreground bg-secondary rounded-lg p-1.5" />
            <div><p className="text-sm font-semibold text-foreground">Configurações</p><p className="text-xs text-muted-foreground">Painel administrativo</p></div>
          </button>
        </div>
      </div>

      {/* Modal: Rejeitar */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={e => e.target === e.currentTarget && setRejectModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 animate-fade-in">
            <h3 className="text-base font-bold mb-3 text-foreground" style={{ fontFamily: "Manrope" }}>Motivo da Rejeição</h3>
            <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={3} placeholder="Descreva o motivo (opcional)"
              className="w-full border border-border rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none" data-testid="reject-reason-input" />
            <div className="flex gap-3 mt-4">
              <button onClick={() => setRejectModal(null)} className="flex-1 py-2.5 border border-border rounded-xl text-sm font-medium hover:bg-secondary transition-all">Cancelar</button>
              <button onClick={() => handleValidate(rejectModal, "rejeitado", rejectReason)}
                disabled={validating === rejectModal}
                className="flex-1 py-2.5 bg-destructive text-white rounded-xl text-sm font-medium hover:bg-destructive/90 transition-all disabled:opacity-60"
                data-testid="confirm-reject-btn">
                {validating === rejectModal ? "Rejeitando..." : "Confirmar Rejeição"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Editar função do usuário */}
      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={e => e.target === e.currentTarget && setEditingUser(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 animate-fade-in">
            <h3 className="text-base font-bold mb-1 text-foreground" style={{ fontFamily: "Manrope" }}>Alterar Função</h3>
            <p className="text-sm text-muted-foreground mb-4">{editingUser.full_name || editingUser.name}</p>
            <select value={editRole} onChange={e => setEditRole(e.target.value)}
              className="w-full h-12 border border-border rounded-xl px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 mb-4"
              data-testid="edit-role-select">
              <option value="">Selecionar função</option>
              {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <p className="text-xs text-yellow-800">Ao promover um profissional a <strong>Secretário(a)</strong> ou <strong>Coordenador(a)</strong>, ele terá acesso à gestão do município: <strong>{editingUser.municipality || "não informado"}</strong></p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setEditingUser(null)} className="flex-1 py-2.5 border border-border rounded-xl text-sm font-medium hover:bg-secondary transition-all">Cancelar</button>
              <button onClick={handleUpdateRole} disabled={saving || !editRole}
                className="flex-1 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:bg-primary/90 transition-all disabled:opacity-60"
                data-testid="save-role-btn">
                {saving ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
